import { notify } from 'src/clients/ApiService';
import { ApiMethods, ApiTags, appAPI } from 'src/services/api/appApi';
import {
  CustomFieldEntity,
  CustomFieldEntityType,
  CustomFieldOption,
} from 'src/store/clients/types';
import { ensureApiError } from 'src/utils/Errors';

export const customFieldsApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    getCustomFields: build.query<
      CustomFieldEntity,
      { type: CustomFieldEntityType }
    >({
      query: ({ type }) => ({
        path: `/entities/custom-fields?type=${type}`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: [ApiTags.customFields],
    }),
    updateCustomFields: build.mutation<
      CustomFieldEntity,
      { customFields: CustomFieldEntity; type: CustomFieldEntityType }
    >({
      query: ({ customFields, type }) => ({
        path: `/entities/custom-fields?type=${type}`,
        method: ApiMethods.put,
        options: {
          body: customFields,
        },
      }),
      async onQueryStarted({ type }, { dispatch, queryFulfilled }) {
        try {
          const result = await queryFulfilled;
          const updatedCustomFields = result.data;

          // Update the cache for the specific type
          dispatch(
            customFieldsApi.util.updateQueryData(
              'getCustomFields',
              { type },
              (draft) => {
                Object.keys(draft).forEach((key) => {
                  if (!(key in updatedCustomFields)) {
                    delete draft[key as keyof CustomFieldEntity];
                  }
                });
                Object.assign(draft, updatedCustomFields);
              },
            ),
          );

          notify({
            status: 'success',
            successMessage: 'Custom Fields have been updated',
            dispatch,
          });
        } catch (err) {
          const { message: errorMessage } = ensureApiError(err);
          console.error('Error updating custom fields:', errorMessage);
          notify({
            status: 'error',
            errorMessage: errorMessage || 'Custom Fields could not be updated',
            error: err,
            dispatch,
          });
        }
      },
    }),
  }),
});

export const { useGetCustomFieldsQuery, useUpdateCustomFieldsMutation } =
  customFieldsApi;

// Empty object constant to prevent re-rendering when additionalFields is undefined
const emptyPortalCustomFields: Record<string, CustomFieldOption> = {};

// Custom hook that handles undefined additionalFields to prevent re-rendering
export function useCustomFields(type: CustomFieldEntityType) {
  const { data: customFields } = useGetCustomFieldsQuery({
    type: type,
  });

  const portalCustomFields: Record<string, CustomFieldOption> =
    customFields?.additionalFields || emptyPortalCustomFields;

  return portalCustomFields;
}
